/**
 * CountingQuiz Adult Renderer
 * Clean digit counters with chevron up/down buttons — no Lottie, no student-style animations.
 * Uses the useCountingQuiz headless hook for all logic.
 */

import { cn } from '../../../lib/utils'
import { KatexText, QuizResultFeedback, SubmitButton, QuizSolveDescription, QuizMedia, QuizTypeBadge } from '../shared'
import type { QuizSolveResult } from '../../../types/legacyQuiz'
import type { CountingQuizData } from '../hooks'
import { formatText } from '../../../utils/quiz-parser'
import { useCountingQuiz } from '../hooks'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { ChevronUp, ChevronDown } from 'lucide-react'

interface CountingQuizProps {
  quizId: string | number
  quiz: CountingQuizData
  onSolve?: (quizId: string | number, answers: string[], vars: Record<string, unknown>, elapsedTime: number) => void
  solveResult?: QuizSolveResult | null
  showReallyTrue?: boolean
  isViewSolved?: boolean
  initialAnswers?: string[]
  submitLabel?: string
  hideSubmitButton?: boolean
  onAnswerChange?: (answers: string[]) => void
  disableSound?: boolean
  difficultyLevel?: number | null
  /** G3 — true면 풀이 후 해설(QuizSolveDescription)을 숨긴다(정오답 피드백은 유지). 기본 false=현행 노출. */
  hideExplanation?: boolean
}

export function CountingAdult({
  quizId,
  quiz,
  onSolve,
  solveResult,
  showReallyTrue = false,
  isViewSolved = false,
  initialAnswers: initialAnswersProp,
  submitLabel,
  hideSubmitButton = false,
  onAnswerChange,
  disableSound = false,
  difficultyLevel,
  hideExplanation = false,
}: CountingQuizProps) {
  const { t } = useTranslation('quiz')

  const {
    displayValues,
    isSolved,
    canSubmit,
    shouldShowCorrectAnswers,
    isShowingCorrectAnswers,
    setIsShowingCorrectAnswers,
    correctDigits,
    isDisabled,
    handleUp,
    handleDown,
    handleSubmit,
  } = useCountingQuiz({
    quizId,
    quiz,
    onSolve,
    solveResult,
    showReallyTrue,
    isViewSolved,
    initialAnswers: initialAnswersProp,
    onAnswerChange,
  })

  return (
    <div className="space-y-6">
      {/* Quiz type badge */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-2"
      >
        <QuizTypeBadge
          quizId={quizId}
          typeLabel={t('types.counting.label')}
          difficultyLevel={difficultyLevel}
        />
      </motion.div>

      {/* Title + problem + media */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.1 }}
        className={cn(
          'p-6 bg-linear-to-br to-gray-50 rounded-2xl from-slate-50',
          'border border-gray-100 shadow-2xs',
          'text-2xl font-medium leading-relaxed text-gray-900'
        )}
      >
        <KatexText text={quiz.title} />
        {quiz.problem && (() => {
          const cleanProblem = quiz.problem.replace(/\{\{\}\}/g, '').trim()
          if (!cleanProblem) return null
          return (
            <div className="mt-4 text-xl">
              {formatText(cleanProblem, `${quizId}-problem`, quiz.additionalImages ?? undefined)}
            </div>
          )
        })()}
        <QuizMedia image={quiz.image} imageMobile={quiz.imageMobile} desmos={quiz.desmos} vars={quiz.vars} />
      </motion.div>

      {/* Digit counters */}
      <div className="flex items-center justify-center gap-4 mt-6">
        {displayValues.map((value, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: index * 0.05 }}
            className={cn(
              'inline-flex flex-col items-center rounded-xl border-2 overflow-hidden',
              shouldShowCorrectAnswers || (isSolved && solveResult?.correct)
                ? 'border-green-300 bg-green-50'
                : isSolved && !solveResult?.correct
                ? 'border-red-300 bg-red-50'
                : 'border-gray-300 bg-white'
            )}
          >
            {/* Up button */}
            <button
              onClick={() => handleUp(index)}
              disabled={isDisabled}
              className={cn(
                'w-16 h-10 flex items-center justify-center transition-colors',
                !isDisabled
                  ? 'hover:bg-blue-50 text-blue-500 active:bg-blue-100'
                  : 'text-gray-300 cursor-not-allowed'
              )}
              aria-label={t('types.counting.incrementDigit', { position: index + 1 })}
            >
              <ChevronUp className="w-6 h-6" />
            </button>

            {/* Digit display */}
            <div className={cn(
              'w-16 h-16 flex items-center justify-center text-4xl font-semibold border-y',
              shouldShowCorrectAnswers || (isSolved && solveResult?.correct)
                ? 'text-green-600 border-green-200'
                : isSolved && !solveResult?.correct
                ? 'text-red-500 border-red-200'
                : 'text-gray-800 border-gray-200'
            )}>
              {value}
            </div>

            {/* Down button */}
            <button
              onClick={() => handleDown(index)}
              disabled={isDisabled}
              className={cn(
                'w-16 h-10 flex items-center justify-center transition-colors',
                !isDisabled
                  ? 'hover:bg-blue-50 text-blue-500 active:bg-blue-100'
                  : 'text-gray-300 cursor-not-allowed'
              )}
              aria-label={t('types.counting.decrementDigit', { position: index + 1 })}
            >
              <ChevronDown className="w-6 h-6" />
            </button>
          </motion.div>
        ))}
      </div>

      {/* Submit button */}
      {!isSolved && !isViewSolved && !hideSubmitButton && (
        <SubmitButton
          onClick={handleSubmit}
          disabled={!canSubmit}
          label={submitLabel}
        />
      )}

      {/* Result feedback */}
      {isSolved && (
        <QuizResultFeedback
          isCorrect={solveResult?.correct ?? false}
          show={isSolved}
          correctAnswer={shouldShowCorrectAnswers && correctDigits ? correctDigits.join('') : undefined}
          disableSound={disableSound}
          isViewSolved={isViewSolved}
        />
      )}

      {/* Solve description */}
      {isSolved && solveResult?.description && !hideExplanation && (
        <QuizSolveDescription
          description={solveResult.description}
          commentaryImages={quiz.commentaryImages}
          vars={quiz.vars}
          id={`${quizId}-desc`}
          actionVisible={!solveResult.correct}
          actionLabel={isShowingCorrectAnswers ? t('common.viewMyAnswer') : t('common.viewCorrectAnswer')}
          onActionClick={() => setIsShowingCorrectAnswers(v => !v)}
        />
      )}
    </div>
  )
}
